import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Mail, Lock, Inbox, LineChart, BookOpen } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import DateRangeBar from "@/components/admin/DateRangeBar";
import SummaryAndExport from "@/components/admin/SummaryAndExport";
import FunnelPanel from "@/components/admin/FunnelPanel";
import KeywordTracker from "@/components/admin/KeywordTracker";
import BlogManager from "@/components/admin/BlogManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtParam } from "@/lib/adminUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "triaxis_admin_token";

export default function AdminLeads() {
  useSeo({ title: "Admin · Leads — Tri-Axis XYZ", description: "Admin dashboard" });
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [range, setRange] = useState({ from: undefined, to: undefined });

  const load = useCallback(async (tkn = token, r = range) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": tkn };
      const params = {};
      if (r?.from) params.from = fmtParam(r.from);
      if (r?.to) params.to = fmtParam(r.to);
      const [c, q, e] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers, params }),
        axios.get(`${API}/admin/quotes`, { headers, params }),
        axios.get(`${API}/admin/errors`, { headers }),
      ]);
      setContacts(c.data); setQuotes(q.data); setErrors(e.data);
      sessionStorage.setItem(TOKEN_KEY, tkn);
      setAuthed(true);
    } catch {
      toast.error("Invalid admin token or bad date range.");
      if (!sessionStorage.getItem(TOKEN_KEY)) setAuthed(false);
    } finally { setLoading(false); }
  }, [token, range]);

  useEffect(() => {
    // Mount-only: bootstrap data once if a token already exists in sessionStorage.
    // `load` and `authed` are intentionally NOT in deps — adding them would loop
    // (load mutates authed → re-fire → infinite reload).
    if (authed) load();
  }, []);

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="w-full max-w-sm border border-slate-200 p-8 bg-white">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2"><Lock className="h-3 w-3" /> Admin access</div>
          <h1 className="display mt-3 text-3xl font-extrabold tracking-tighter text-slate-900">Leads inbox</h1>
          <label htmlFor="tk" className="mt-6 mono text-[10px] tracking-[0.22em] uppercase text-slate-500 block">Admin token</label>
          <input id="tk" type="password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="admin-token-input"
            className="mt-2 w-full border border-slate-300 focus:border-topo outline-none p-3 text-sm rounded-sm bg-white" placeholder="paste token" />
          <button type="submit" data-testid="admin-login-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-5 py-3">
            <ShieldCheck className="h-4 w-4" /> Sign in
          </button>
        </form>
      </div>
    );
  }

  const applyFilter = (r) => { setRange(r); load(token, r); };
  const clearFilter = () => { const empty = { from: undefined, to: undefined }; setRange(empty); load(token, empty); };
  const rangeActive = !!(range?.from || range?.to);

  const toggleContacted = async (kind, id, current) => {
    try {
      await axios.patch(`${API}/admin/${kind}s/${id}`, { contacted: !current }, { headers: { "X-Admin-Token": token } });
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="page-admin-leads" className="mx-auto max-w-7xl px-6 lg:px-10 py-12">
      <DateRangeBar range={range} onApply={applyFilter} onClear={clearFilter} loading={loading} />
      <Tabs defaultValue="leads" className="w-full">
        <TabsList className="mb-8 h-auto rounded-none bg-transparent p-0 border-b border-slate-200 w-full justify-start gap-6" data-testid="admin-tabs">
          {[["leads", "Leads inbox", Inbox], ["keywords", "Keyword tracker", LineChart], ["blog", "Blog posts", BookOpen]].map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}
              className="rounded-none px-0 pb-3 pt-0 text-sm font-semibold text-slate-500 border-b-2 border-transparent data-[state=active]:border-orange-survey data-[state=active]:text-slate-900 data-[state=active]:shadow-none bg-transparent data-[state=active]:bg-transparent gap-2">
              <Icon className="h-4 w-4" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="keywords"><KeywordTracker token={token} range={range} /></TabsContent>
        <TabsContent value="blog"><BlogManager token={token} /></TabsContent>
        <TabsContent value="leads">
      <SummaryAndExport contacts={contacts} quotes={quotes} range={range} />
      <FunnelPanel contacts={contacts} quotes={quotes} rangeActive={rangeActive} />
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo">Admin · Leads inbox</div>
          <h1 className="display mt-2 text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            {contacts.length + quotes.length} total leads · {errors.length} client errors
          </h1>
        </div>
        <button onClick={() => load()} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-sm border border-slate-300 hover:border-slate-900 text-slate-900 text-sm font-semibold px-4 py-2 bg-white">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <Section title="Contact submissions" count={contacts.length} testid="contacts-table">
        <Table cols={["When", "Name", "Email", "Source", "Service", "Message", "Status"]}>
          {contacts.map((r) => (
            <tr key={r.id} className={`border-t border-slate-100 ${r.contacted ? "bg-emerald-50/40" : ""}`}>
              <td className="p-3 mono text-xs text-slate-500 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
              <td className="p-3 text-sm font-semibold text-slate-900">{r.name}</td>
              <td className="p-3 text-sm"><a className="text-topo hover:underline" href={`mailto:${r.email}`}><Mail className="inline h-3 w-3 mr-1" />{r.email}</a></td>
              <td className="p-3 mono text-[10px] tracking-[0.12em] uppercase text-emerald-700">{r.source || "direct"}</td>
              <td className="p-3 text-sm text-slate-700">{r.service || "—"}</td>
              <td className="p-3 text-xs text-slate-600 max-w-xs truncate">{r.message}</td>
              <td className="p-3">
                <ContactedToggle testid={`toggle-contact-${r.id}`} value={r.contacted} onClick={() => toggleContacted("contact", r.id, r.contacted)} />
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section title="Quote requests" count={quotes.length} testid="quotes-table">
        <Table cols={["When", "Name", "Email", "Source", "Service slug", "Details", "Status"]}>
          {quotes.map((r) => (
            <tr key={r.id} className={`border-t border-slate-100 ${r.contacted ? "bg-emerald-50/40" : ""}`}>
              <td className="p-3 mono text-xs text-slate-500 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
              <td className="p-3 text-sm font-semibold text-slate-900">{r.name}</td>
              <td className="p-3 text-sm text-topo">{r.email}</td>
              <td className="p-3 mono text-[10px] tracking-[0.12em] uppercase text-emerald-700">{r.source || "quote-form"}</td>
              <td className="p-3 mono text-xs text-emerald-700">{r.service_slug}</td>
              <td className="p-3 text-xs text-slate-600 max-w-xs truncate">{r.project_details}</td>
              <td className="p-3">
                <ContactedToggle testid={`toggle-quote-${r.id}`} value={r.contacted} onClick={() => toggleContacted("quote", r.id, r.contacted)} />
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      <Section title="Client errors" count={errors.length} testid="errors-table">
        <Table cols={["When", "URL", "Message"]}>
          {errors.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="p-3 mono text-xs text-slate-500 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
              <td className="p-3 mono text-xs text-slate-700 max-w-xs truncate">{r.url}</td>
              <td className="p-3 text-xs text-rose-700 max-w-md truncate">{r.message}</td>
            </tr>
          ))}
        </Table>
      </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, count, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="display text-xl font-extrabold tracking-tight text-slate-900">{title}</h2>
        <div className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{count} entries</div>
      </div>
      <div className="border border-slate-200 bg-white overflow-x-auto">{children}</div>
    </section>
  );
}

function Table({ cols, children }) {
  return (
    <table className="w-full text-left">
      <thead className="bg-paper-dark/40">
        <tr>{cols.map((c) => <th key={c} className="p-3 mono text-[10px] tracking-[0.18em] uppercase text-slate-500">{c}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function ContactedToggle({ testid, value, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`mono text-[10px] tracking-[0.12em] uppercase px-2 py-1 rounded-sm border ${value ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
    >
      {value ? "✓ Contacted" : "Mark contacted"}
    </button>
  );
}
